import { useTranslation } from 'react-i18next'
import { Icon } from '../ui/icon'

interface HelpModalProps {
  isOpen: boolean
  onClose: () => void
}

export const HelpModal = ({ isOpen, onClose }: HelpModalProps) => {
  const { t } = useTranslation()

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-bg-surface rounded-2xl shadow-2xl border border-border-default w-full max-w-lg mx-4 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-default">
          <h2 className="text-lg font-semibold text-fg-primary">{t('help.title')}</h2>
          <button onClick={onClose} className="rounded-full p-1.5 text-fg-muted hover:text-fg-primary hover:bg-interactive-hover transition-colors">
            <Icon icon="x" size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          <section>
            <h3 className="text-sm font-semibold text-fg-primary mb-3">{t('help.keyboardShortcuts')}</h3>
            <div className="space-y-1 text-sm">
              {[
                ['Ctrl+S', t('help.shortcutSave')],
                ['Ctrl+Shift+S', t('help.shortcutSaveAs')],
                ['Ctrl+O', t('help.shortcutOpen')],
                ['Ctrl+P', t('help.shortcutPrint')],
                ['Ctrl+Z', t('help.shortcutUndo')],
                ['Ctrl+Shift+Z', t('help.shortcutRedo')],
                ['Ctrl+= / Ctrl+-', t('help.shortcutZoom')],
                ['Ctrl+0', t('help.shortcutFitPage')],
                ['← →', t('help.shortcutNavigate')],
                ['Delete', t('help.shortcutDelete')],
                ['Escape', t('help.shortcutDeselect')],
              ].map(([key, desc]) => (
                <div key={key} className="flex items-center gap-3">
                  <kbd className="px-2 py-0.5 rounded bg-bg-surface-alt border border-border-default text-xs font-mono text-fg-secondary min-w-[90px] text-center">{key}</kbd>
                  <span className="text-fg-muted">{desc}</span>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-fg-primary mb-3">{t('help.annotationTools')}</h3>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {[
                ['highlight', t('tooltips.highlight')],
                ['underline', t('tooltips.underline')],
                ['strikethrough', t('tooltips.strikethrough')],
                ['squiggly', t('tooltips.squigglyUnderline')],
                ['pencil', t('tooltips.pencil')],
                ['highlighter', t('tooltips.highlighter')],
                ['textBox', t('tooltips.textBox')],
                ['stickyNote', t('tooltips.stickyNote')],
                ['rectangle', t('tooltips.rectangle')],
                ['oval', t('tooltips.oval')],
                ['line', t('tooltips.line')],
                ['arrow', t('tooltips.arrow')],
                ['polygon', t('tooltips.polygon')],
                ['polyline', t('tooltips.polyline')],
              ].map(([icon, name]: [string, any]) => (
                <div key={icon} className="flex items-center gap-2 px-2 py-1 rounded-lg bg-bg-surface-alt">
                  <Icon icon={icon} size={14} />
                  <span className="text-fg-secondary">{name}</span>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-fg-primary mb-3">{t('help.ocrGuide')}</h3>
            <p className="text-xs text-fg-muted leading-relaxed">{t('help.ocrDescription')}</p>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-fg-primary mb-3">{t('help.selectAndDrag')}</h3>
            <p className="text-xs text-fg-muted leading-relaxed">{t('help.selectAndDragHint')}</p>
          </section>
        </div>
      </div>
    </div>
  )
}
